/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { getSettings, useSettings } from '@/data/Settings';
import { MAX_ITEMS_LEAFPAGE, RADIUS_UOM, SORTBY, STLOC_ALL_STORES_DEF_QUERY, STORE_LIST_RADIUS_LEAFPAGE } from '../constants/storeLocator';
import { ID } from '@/data/types/Basic';
import { error as logError } from '@/data/utils/loggerUtil';
import { StorelocatorStorelocatorItem } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';
import { getUser } from '../User';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { shrink, expand as expandProps } from '@/data/utils/keyUtil';
import { getLocalization } from '@/data/Localization-Server';
import { getChildContentItems } from '@/data/utils/getChildContentItems';
import { getContentRecommendation } from '@/data/Content/ContentRecommendation-Server';
import { ContentProps } from '@/data/types/ContentProps';
import { STORE_ID } from '../config/STORE_ID';
import { Translation } from 'integration/generated/translations';
import { useLocalization } from '@/data/Localization';
import { createDisplayStoreDetails } from '../utils/storeUtil';
import { customTransactionsStoreLocator } from 'integration/generated/customTransactions';
import { fetcher as storesByLocationFetcher } from './StoreLocator';
import { CityStreet, StoreDetails } from '../types/Store';
import { useMemo } from 'react';
import { findStoreAttributeByNameStartsWith, findStoreAttributeValueByName } from '../utils/StoreStock';


const DATA_KEY = 'DATA_KEY_SOTRE_LOCATOR_STREET_PAGE';

export type StoreLocatorStreetPage = {
    store: StoreDetails;
    storeIdentifier: string;
    nearbyStores: StoreDetails[];
    isRedirect: boolean;
}

const dataMap = (data: {
	store:StorelocatorStorelocatorItem, 
	nearbyStores:StorelocatorStorelocatorItem[]
}, storeLocatorRoute?: string) : StoreLocatorStreetPage => {
	const store = createDisplayStoreDetails(data?.store, storeLocatorRoute);
	return {
		store,
		storeIdentifier:store?.physicalStoreName??"",
		nearbyStores:data?.nearbyStores?.map((ps) => createDisplayStoreDetails(ps, storeLocatorRoute))??[],
		isRedirect: store?.id?false:true
	}
}

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			props: {
				storeId: string;
				cityToken:string;
				streetToken:string;
				query: {
					[key: string]: string | boolean | ID[] | number;
				}
			},
			params: RequestParams
		): Promise<any | undefined> => {
			
			try {
				const { storeId, cityToken, streetToken, query } = props;
				const cityTokenQ = "speelgoedwinkel-"+cityToken;
				const data = await customTransactionsStoreLocator(pub).storeLocatorFindStoresByCityStreet(
					storeId,
					cityTokenQ,
					streetToken,
					query,
					params
				);
				const store = data?.PhysicalStore?.at(0);
				if(store){
					const nearByStoresData = await storesByLocationFetcher(false)(
						storeId,
						store.latitude?.trim() as string,
						store.longitude?.trim() as string,
						//{...query,
						{
							radius:STORE_LIST_RADIUS_LEAFPAGE,
							maxItems:MAX_ITEMS_LEAFPAGE,
							radiusUOM: RADIUS_UOM,
							profileName: 'IBM_Store_Details',
							sortBy: SORTBY,
							siteLevelStoreSearch: 'false',
						},
						{}
					)
					const nearbyStores = nearByStoresData?.PhysicalStore;
					return {
						store,
						nearbyStores
					}
				}else{
					return undefined;
				}
			} catch (error) {
				logError(context?.req, 'StoreLocator: fetcher: error: %o', error);
			}
		};

export const getStoreLocatorStreetPage = async ({ cache, id, context, properties }: ContentProps) => {
	const path = context?.params?.path;
	const streetToken = path ?.at(-1)?.trim() as string;
	const cityToken = path?.at(-2)?.replace('speelgoedwinkel-','')?.trim() as string;
	const user = await getUser(cache, context);
	const settings = await getSettings(cache, context);
	const { StoreLocator } = await getLocalization(cache, context.locale || 'en-US', 'Routes');
	const storeLocatorRoute = (StoreLocator as Translation)?.route as string;
	await getLocalization(cache, context.locale || 'nl-NL', 'StoreLocator')

	const props = {
		storeId: settings?.storeId ?? STORE_ID,
		cityToken,
		streetToken,
		query: STLOC_ALL_STORES_DEF_QUERY
	};

	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstable_serialize([shrink(props), DATA_KEY]);
	let rc:StoreLocatorStreetPage = {} as StoreLocatorStreetPage;
	const value = cache.get(key, cacheScope);
	if(value){
		rc = await value;
	}else{
		rc = dataMap(await fetcher(false, context)(props, {}), storeLocatorRoute);
	}

	const {storeIdentifier} = rc;

	await Promise.all([
		...getChildContentItems(id).map((contentProperties) => {
			contentProperties = { ...contentProperties, emsName: storeIdentifier + contentProperties.emsName }
			return getContentRecommendation({
				cache,
				id,
				context,
				properties: { ...properties, ...contentProperties },
			})
		}
		),
	]);

	cache.set(key, rc, cacheScope);
	return rc;
}

export const useStoreLocatorStreetPage = (cityStreet:CityStreet) => {
	const { settings } = useSettings();
	const { StoreLocator: storeLocatorRoute } = useLocalization('Routes');
	const { data, isLoading } = useSWR(
		cityStreet.city && cityStreet.street ? [
			shrink({
				storeId: settings?.storeId ?? STORE_ID,
				cityToken:cityStreet.city,
				streetToken:cityStreet.street,
				query: STLOC_ALL_STORES_DEF_QUERY
			}),
			DATA_KEY,
		] : null
		,
		async ([props]) => dataMap(await fetcher(false)(expandProps(props), {}), storeLocatorRoute?.route.t())
	);

	const store = data?.store as StoreDetails;

	const storeStreetData = useMemo(()=>{
		const today = new Date(); 
		const todayDay = today.toLocaleDateString("nl-NL", { weekday: 'long' }); 
		const formatter = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
		const formattedDate = formatter.format(today);
		const specialTimes = findStoreAttributeByNameStartsWith(store?.attributes,'specialopen');
		let StoreLocatorSpecialOpeningMaxCount:number = parseInt(settings?.userData?.StoreLocatorSpecialOpeningMaxCount);
		let filteredSpecialTimes = specialTimes?.filter(attr => attr.value !== '0').slice(0,StoreLocatorSpecialOpeningMaxCount);
		const specialHoursToday = filteredSpecialTimes?.find((spDate)=>spDate.displayName?.includes(formattedDate));
		const todayTimes = store?.attributes?.find((attr)=> (attr.displayName?.toLowerCase() == todayDay?.toLowerCase() && attr.name.startsWith('open')));
		const specialEvents = [
			...(findStoreAttributeByNameStartsWith(store?.attributes, 'specialaction') ?? []),
			...(findStoreAttributeByNameStartsWith(store?.attributes, 'actiontime') ?? [])
		].filter(entry =>
			(entry.displayValue?.toLowerCase() !== 'gesloten') &&
			(entry.value !== '0')
		);
		return {
			isClickCollectAllowed:findStoreAttributeValueByName(store?.attributes, 'storepickallowed')==='1',
			isDHLServicePoint:findStoreAttributeValueByName(store?.attributes, 'dhlservicepoint','displayValue')?.toLowerCase()==='is aanwezig',
			isNintendoCorner:findStoreAttributeValueByName(store?.attributes, 'nintendocorner','displayValue')?.toLowerCase()==='is aanwezig',
			isFanafdeling:findStoreAttributeValueByName(store?.attributes, 'fanafdeling','displayValue')?.toLowerCase()==='is aanwezig',
			openingTimes : findStoreAttributeByNameStartsWith(store?.attributes,'opentime'),
			lessSpecialTimes : filteredSpecialTimes?.slice(0, 10),
			storeClosedToday: (specialHoursToday?.displayValue?.toLowerCase() === 'gesloten') &&
			(todayTimes?.displayValue?.toLowerCase() === 'gesloten'),
			specialHoursToday,
			todayTimes,
			specialEvents
		}
	},[store]);

	return {
		storeStreetData:{
			...store,
			...storeStreetData
		},
		nearbyStores: data?.nearbyStores??[],
		isRedirect:data?.isRedirect??false,
		isLoading
	}
};